/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, useMemo } from 'react';
import { ModelManager } from '@adobe/aem-spa-page-model-manager';
import path from 'path';
import '@marriott/global-styles/dist/marriot.global.css';
import '@glidejs/glide/src/assets/sass/glide.core.scss';
import '@glidejs/glide/src/assets/sass/glide.theme.scss';
import '../styles/styles.css';
import operationSignatures from './_constants/operationSignatures.json';
import { defineComponentMapping, getAllowedComponentsForNonAuthorMode } from '../import-components';
import { PageContext, pageInitialData } from '@marriott/mi-martech-components';
import { usePersistentGlobalStore, useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { CustomModelClient, getEnvProp, UserSessionProvider } from '@marriott/mi-headless-utils';
import NextApp from 'next/app';
import { sessionTimeoutManager } from '@marriott/shared/mi-helper-utils';
import { FEATURE_FLAGS, fetchFlag, TARGETS } from '@marriott/mi-feature-flag-utils';

path.resolve('./next.config.js');
declare global {
  interface Window {
    jQuery: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    $: any;
    deployedEnvType: string;
  }
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const modelClient = new CustomModelClient(process.env.NEXT_PUBLIC_AEM_HOST);
if (process.env.NODE_ENV !== 'test') {
  ModelManager.initializeAsync({
    modelClient,
  });
}

/**
 * returns the lang-selector modal if it's present in top-nav
 * else return's undefined if there is no top-nav authored or if there is not lang selector autored ....
 */
const getLanguageSelectorModalFromTopNavigation = (model: any) => {
  const allItems = model?.cqItems?.root?.[':items']?.responsivegrid?.[':items'];

  // Find the headerResponsivegrid item
  const headerResponsivegrid =
    allItems?.['headerResponsivegrid']?.[':items']?.root?.[':items']?.responsivegrid?.[':items'];

  //added fallback if there is no headerResponsivegrid
  if (!headerResponsivegrid) return undefined;

  // Find the top-navigation item
  const topNavigation: any = Object.values(headerResponsivegrid).find((el: any) =>
    el?.[':type']?.includes('top-navigation')
  );

  //added fallback if there is no top-nav
  if (!topNavigation?.[':items']) return undefined;

  // Find the language-selector inside top-navigation
  return Object.values(topNavigation[':items']).find((el: any) => el?.[':type']?.includes('language-selector'));
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const App = function (props: any) {
  const { Component, pageProps } = props;
  //const { model } = pageProps;
  const {
    sessionData,
    SESSION_APP_GET_CALL_URL,
    SESSION_APP_POST_CALL_URL,
    resolvedUrl,
    requestId,
    currentLocale,
    getFeatureFlag,
    headersData,
    cookies,
    query,
    model,
    isAuthorMode,
    MI_DATALAYER_APP_URL,
    datalayerParams,
    datalayerProperties,
    NEXT_PUBLIC_PRE_PROCESSING_ADF_URL,
    interceptorResponse,
    NEXT_PUBLIC_PRE_PROCESSING_RELATIVE_ADF_URL,
    sessionFetchedData, // session data fetched from productspecific call please maintain the same name
    NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT,
    GOOGLE_MAP_API_KEY,
    BAIDU_MAP_API_KEY,
    DEPLOYED_ENV_CONFIG,
    ENABLE_ADV_SEARCH_DATALAYER_CALL,
    NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
    GROUPS_DEV_URL,
    GROUPS_TEST_URL,
    isDTT,
    disableDistanceFromLocation,
    NEXT_PUBLIC_PRE_PROCESSING_URL,
    CACHE_CLEAR_URL,
    ENABLE_TARGET_SEGMENT_CALL,
    isBulgariDomain,
    NEXT_PUBLIC_BULGARI_URL,
    featureFlagLogo,
    findHotelsRemoteSPA,
    featureFlagSessionTimeout,
    sessionTimeoutMs,
    apolloEnvVars,
  } = pageProps;

  const preHeader: Record<string, string> = {
    'Content-Type': 'application/json',
    'x-request-id': headersData?.['x-request-id'] || '',
    Cookie: `sessionID=${cookies?.sessionID};UserIdToken=${cookies?.UserIdToken ? cookies?.UserIdToken : ''}`,
    'X-Host': headersData ? headersData['x-host'] : '',
    'accept-language': headersData?.['accept-language'] || process.env['NEXT_PUBLIC_DEFAULT_LANG'] || '',
  };

  const modelAllowedComponents = model?.cqItems?.root?.[':items']?.responsivegrid?.allowedComponents?.components;
  // Wrap with useMemo to avoid recomputing on every render
  const allowedComponents = useMemo(() => {
    if (!model) return undefined;
    if ((modelAllowedComponents?.length ?? 0) > 0 || isAuthorMode) return modelAllowedComponents;
    return getAllowedComponentsForNonAuthorMode(model);
  }, [model, modelAllowedComponents, isAuthorMode]);

  const updateGlobalData = usePersistentGlobalStore((state: any) => state.updateGlobalData);
  useEffect(() => {
    updateGlobalData('sessionData', pageProps?.sessionData);
  }, [pageProps?.sessionData, updateGlobalData]);

  const languageSelectorModal = getLanguageSelectorModalFromTopNavigation(model);

  const mergeEnvVars = useClientEnvVarsStore(state => state.mergeEnvVars);

  // Set environment variables using mergeEnvVars in useEffect to avoid mutation
  useEffect(() => {
    const envVarsToMerge = {
      ...(apolloEnvVars || {}),
      NEXT_PUBLIC_ENABLE_WEBSDK: getEnvProp('NEXT_PUBLIC_ENABLE_WEBSDK') === 'true' && model?.enableWebSDK === 'true',
      ENABLE_ACDL: true,
      SKIP_MVP_OFFERS: getEnvProp('SKIP_MVP_OFFERS') || 'true',
    };

    if (mergeEnvVars) {
      mergeEnvVars(envVarsToMerge);
    }
  }, []);

  defineComponentMapping(allowedComponents, pageProps.isAuthorMode, pageProps, languageSelectorModal);

  const [pageState, setPageState] = useState<any>({ ...pageInitialData, authorMode: pageProps.isAuthorMode });
  const [sessionDataState, setSessionDataState] = useState<any>(sessionData || sessionFetchedData);

  // Get sessionData from global store
  const isClient = typeof window !== 'undefined';

  // Check if user is logged in - only on client side
  const isLoggedIn = React.useMemo(() => {
    if (!isClient) return false;

    const sessionData = (window as any).sessionData;
    return (
      sessionData?.data?.AriesCommon?.memState === 'authenticated' ||
      sessionData?.cacheData?.data?.AriesCommon?.memState === 'authenticated'
    );
  }, [isClient]);

  useEffect(() => {
    if (
      !isAuthorMode &&
      typeof window !== 'undefined' &&
      featureFlagSessionTimeout['experience_mcom_session_timeout_migration'] &&
      sessionTimeoutMs > 0
    ) {
      console.log('Session Timeout - initializing with timeout:', sessionTimeoutMs);
      sessionTimeoutManager.initialize({
        timeoutMs: sessionTimeoutMs,
        isLoggedIn,
        onTimeout() {
          if (typeof window === 'undefined') return;

          console.log('Session related App specific functionality can be added here');
        },
        onUserActivity() {
          console.log('User activity detected - session timer reset');
        },
      });
    }
  }, [sessionTimeoutMs, isLoggedIn, isAuthorMode, featureFlagSessionTimeout]);

  const memoPageContexValue = useMemo(() => {
    const host = headersData?.['x-host'];
    const updateComponentState = ({
      componentName,
      newState,
    }: {
      componentName: string;
      newState: Record<string, any>;
    }) => {
      setPageState((preState: any) => {
        return { ...preState, [componentName]: newState };
      });
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updatePageErrors = ({ componentName, errors }: { componentName: string; errors: string[] }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setPageState((preState: any) => {
        return {
          ...preState,
          pageLevelErrors: {
            ...preState.pageLevelErrors,
            [componentName]: errors,
          },
        };
      });
    };

    return {
      featureFlagLogo,
      pageState,
      setPageState,
      updateComponentState,
      updatePageErrors,
      sessionData: sessionDataState,
      setSessionData: setSessionDataState,
      resolvedUrl,
      getFeatureFlag,
      requestId,
      currentLocale: isAuthorMode ? 'en-US' : currentLocale?.replace('_', '-'),
      headersData,
      cookies,
      query,
      operationSignatures,
      MI_DATALAYER_APP_URL,
      datalayerParams,
      datalayerProperties,
      NEXT_PUBLIC_PRE_PROCESSING_ADF_URL,
      NEXT_PUBLIC_PRE_PROCESSING_RELATIVE_ADF_URL,
      interceptorResponse,
      preHeader,
      SESSION_APP_GET_CALL_URL: `https://${host}${SESSION_APP_GET_CALL_URL}`,
      SESSION_APP_POST_CALL_URL: `https://${host}${SESSION_APP_POST_CALL_URL}`,
      NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT,
      GOOGLE_MAP_API_KEY,
      BAIDU_MAP_API_KEY,
      DEPLOYED_ENV_CONFIG,
      ENABLE_ADV_SEARCH_DATALAYER_CALL,
      NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
      GROUPS_DEV_URL,
      GROUPS_TEST_URL,
      isDTT,
      disableDistanceFromLocation,
      NEXT_PUBLIC_PRE_PROCESSING_URL: `https://${host}${NEXT_PUBLIC_PRE_PROCESSING_URL}`,
      CACHE_CLEAR_URL: `https://${host}${CACHE_CLEAR_URL}`,
      ENABLE_TARGET_SEGMENT_CALL,
      isBulgariDomain,
      NEXT_PUBLIC_BULGARI_URL,
      isAuthorMode,
      findHotelsRemoteSPA,
    };
  }, [
    featureFlagLogo,
    pageState,
    setPageState,
    sessionData,
    sessionDataState,
    setSessionDataState,
    resolvedUrl,
    getFeatureFlag,
    requestId,
    currentLocale,
    headersData,
    cookies,
    query,
    operationSignatures,
    MI_DATALAYER_APP_URL,
    datalayerParams,
    datalayerProperties,
    NEXT_PUBLIC_PRE_PROCESSING_ADF_URL,
    interceptorResponse,
    preHeader,
    sessionFetchedData,
    NEXT_PUBLIC_PRE_PROCESSING_RELATIVE_ADF_URL,
    SESSION_APP_GET_CALL_URL,
    SESSION_APP_POST_CALL_URL,
    NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT,
    GOOGLE_MAP_API_KEY,
    BAIDU_MAP_API_KEY,
    DEPLOYED_ENV_CONFIG,
    ENABLE_ADV_SEARCH_DATALAYER_CALL,
    NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
    GROUPS_DEV_URL,
    GROUPS_TEST_URL,
    isDTT,
    disableDistanceFromLocation,
    NEXT_PUBLIC_PRE_PROCESSING_URL,
    CACHE_CLEAR_URL,
    NEXT_PUBLIC_BULGARI_URL,
    ENABLE_TARGET_SEGMENT_CALL,
    isBulgariDomain,
    findHotelsRemoteSPA,
  ]);

  return (
    <UserSessionProvider pageProps={pageProps} includePiData={true}>
      <PageContext.Provider value={memoPageContexValue}>
        <Component {...pageProps} />
      </PageContext.Provider>
    </UserSessionProvider>
  );
};

// Fetch feature flag at the app level so it applies to all routes
App.getInitialProps = async (appContext: any) => {
  // Call page's getInitialProps if it exists
  const appProps = await NextApp.getInitialProps(appContext);

  // Fetch session timeout feature flag server-side
  const featureFlagSessionTimeout = await fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_SESSION_TIMEOUT_MIGRATION, {
    target: TARGETS.APP.MARTECH_RENDERER,
    enableStream: true,
  });

  // Get session timeout from runtime env var (only available server-side)
  const sessionTimeoutMs = process.env.NEXT_PUBLIC_SESSION_TIMEOUT_MS
    ? parseInt(process.env.NEXT_PUBLIC_SESSION_TIMEOUT_MS, 10) * 1000
    : 0;

  return {
    ...appProps,
    pageProps: {
      ...appProps.pageProps,
      featureFlagSessionTimeout,
      sessionTimeoutMs,
    },
  };
};

export default App;
